/**
 * SecurityDepositsList — settlement actions on member_security_deposits.
 *
 * Mirrors CreditNotesList: Held rows show Mark Refunded / Mark Adjusted
 * action buttons; Refunded / Applied rows show settlement details inline.
 * Mark Refunded is gated client-side on lockInUntilDate <= today (tooltip
 * shows the unlock date when disabled); backend re-validates with
 * 400 lock_in_not_completed.
 */

import {
  useGetAccountDeposits,
  useSettleAccountDeposit,
  getGetAccountDepositsQueryKey,
  type HeldDeposit,
} from "@workspace/api-client-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { DepositRefundDialog } from "./deposit-refund-dialog";

function fmt(n: number) {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

function statusLabel(s: string): string {
  switch (s) {
    case "Refunded":
      return "Refunded";
    case "Adjusted":
      return "Adjusted (pending)";
    case "Applied":
      return "Applied";
    default:
      return "Held";
  }
}

function statusClass(s: string): string {
  switch (s) {
    case "Refunded":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "Adjusted":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "Applied":
      return "bg-sky-100 text-sky-800 border-sky-200";
    default:
      return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
}

interface Props {
  accountId: number;
}

export function SecurityDepositsList({ accountId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetAccountDeposits(
    accountId,
    { includeAll: true },
    {
      query: {
        enabled: accountId > 0,
        queryKey: getGetAccountDepositsQueryKey(accountId, { includeAll: true }),
      },
    },
  );
  const settleMutation = useSettleAccountDeposit();
  const [refundTarget, setRefundTarget] = useState<HeldDeposit | null>(null);
  const [settlingId, setSettlingId] = useState<number | null>(null);

  async function onMarkAdjusted(depositId: number) {
    setSettlingId(depositId);
    try {
      await settleMutation.mutateAsync({
        id: accountId,
        depositId,
        data: { settlementStatus: "Adjusted" },
      });
      queryClient.invalidateQueries({
        queryKey: getGetAccountDepositsQueryKey(accountId, { includeAll: true }),
      });
      toast({
        title: "Marked Adjusted.",
        description: "Will auto-apply to the customer's next plan.",
      });
    } catch (err) {
      toast({
        title: "Couldn't update the deposit.",
        description: (err as Error)?.message ?? undefined,
        variant: "destructive",
      });
    } finally {
      setSettlingId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-2 pt-2">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded" />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No security deposits held against this account.
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deposit Ref</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Held On</TableHead>
              <TableHead>Lock-in Until</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Settlement</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => {
              const status = d.settlementStatus ?? "Held";
              const terminal = status === "Refunded" || status === "Applied";
              const lockInOver = !d.lockInUntilDate || d.lockInUntilDate <= today;
              return (
                <TableRow key={d.id}>
                  <TableCell className="font-mono text-sm font-medium">
                    DEP-{String(d.id).padStart(4, "0")}
                  </TableCell>
                  <TableCell className="text-sm">
                    {d.membershipId != null ? (
                      <span className="font-semibold">Plan #{d.membershipId}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.createdAt ? format(new Date(d.createdAt), "dd MMM yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.lockInUntilDate ? format(new Date(d.lockInUntilDate), "dd MMM yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">
                    {fmt(d.amount)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${statusClass(status)}`}
                    >
                      {statusLabel(status)}
                    </Badge>
                    {status === "Refunded" && d.settlementDate && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {d.settlementPaymentMode ? `${d.settlementPaymentMode} · ` : ""}
                        {format(new Date(d.settlementDate), "dd MMM yyyy")}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {terminal ? (
                      status === "Refunded" && d.settlementProofUrl ? (
                        <a
                          href={d.settlementProofUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Proof
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )
                    ) : status === "Adjusted" ? (
                      <span className="text-[10px] text-muted-foreground">
                        Awaiting next plan
                      </span>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={-1}>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={!lockInOver || settlingId === d.id}
                                onClick={() => setRefundTarget(d)}
                              >
                                Mark Refunded
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {!lockInOver && (
                            <TooltipContent>
                              Available after lock-in:{" "}
                              {d.lockInUntilDate
                                ? format(new Date(d.lockInUntilDate), "dd MMM yyyy")
                                : "—"}
                            </TooltipContent>
                          )}
                        </Tooltip>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={settlingId === d.id}
                          onClick={() => onMarkAdjusted(d.id)}
                        >
                          {settlingId === d.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "Mark Adjusted"
                          )}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <DepositRefundDialog
          accountId={accountId}
          deposit={refundTarget}
          open={refundTarget != null}
          onOpenChange={(o) => {
            if (!o) setRefundTarget(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
